import React, { useState } from 'react';
import { Calendar, Clock, GripVertical } from 'lucide-react';
import { DEFAULT_AVATAR_URL } from '../../../constants/assets';
import ServiceDetailModal, { ServiceDetailType, ServiceReview } from '../../../components/Profile/service/ServiceDetailModal';
import { useToast } from '../../../components/UI/Toast';

// Mock Reviews
const MOCK_REVIEWS: ServiceReview[] = [
    { id: 1, avatar: DEFAULT_AVATAR_URL, username: "Alex Chen", rating: 5, content: "非常专业的服务！Sora 生成的视频质量超出了我的预期，光影和物理效果都非常真实。沟通也很顺畅，根据我的反馈改了两版，最终效果很棒。", createdAt: "2 days ago", helpfulCount: 12 },
    { id: 2, avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80", username: "Sarah Li", rating: 4, content: "交付很准时，画质也很清晰。稍微有点遗憾的是人物动作还可以更自然一些，但整体来说性价比很高。", createdAt: "1 week ago", helpfulCount: 5 },
    { id: 3, avatar: "https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=100&q=80", username: "Mike Wang", rating: 5, content: "太强了，不仅技术过硬，审美也很在线。完全不仅是代工，更像是合作创作。强烈推荐！", createdAt: "2 weeks ago", helpfulCount: 28 },
];

// Mock Example Works
const MOCK_EXAMPLES = [
    { id: 101, type: 'video' as const, title: "Cyberpunk City Intro", thumbnailUrl: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80" },
    { id: 102, type: 'video' as const, title: "Product Commercial", thumbnailUrl: "https://images.unsplash.com/photo-1550684848-fac1c5b4e853?w=800&q=80" },
    { id: 103, type: 'image' as const, title: "Concept Art Series", thumbnailUrl: "https://images.unsplash.com/photo-1614728853913-1e32005e319a?w=800&q=80" },
];

// Mock 服务数据（后端不可用时兜底）
export const MOCK_SERVICES: ServiceDetailType[] = [
    {
        id: 1,
        title: "Sora 商业视频定制",
        priceRange: "¥12,000 - ¥30,000",
        description: "提供基于 OpenAI Sora 模型的全流程商业视频定制服务。",
        deliveryTimeRange: "3 - 7 天",
        contentDurationRange: "15s - 60s",
        tags: ["广告/TVC", "Sora", "超写实/电影感", "商业视频"],
        reviews: MOCK_REVIEWS,
        exampleWorks: MOCK_EXAMPLES,
        status: 'active'
    },
    {
        id: 2,
        title: "AIGC 品牌视觉全案",
        priceRange: "¥25,000 - ¥80,000",
        description: "结合 Midjourney V6 与 Stable Diffusion XL 打造的品牌视觉全案服务。",
        deliveryTimeRange: "1 - 3 周",
        contentDurationRange: "不限",
        tags: ["动态Logo/片头", "Midjourney", "Stable Diffusion", "极简/冷淡风"],
        reviews: [MOCK_REVIEWS[0], MOCK_REVIEWS[2]],
        exampleWorks: [MOCK_EXAMPLES[2]],
        status: 'active'
    },
    {
        id: 3,
        title: "Midjourney 风格化训练",
        priceRange: "¥5,000 - ¥12,000",
        description: "提供专业的模型微调训练服务。",
        deliveryTimeRange: "2 - 5 天",
        contentDurationRange: "交付 1 个模型",
        tags: ["LoRA", "二次元/日漫", "Stable Diffusion"],
        reviews: [MOCK_REVIEWS[1]],
        exampleWorks: [MOCK_EXAMPLES[2], MOCK_EXAMPLES[0]],
        status: 'active'
    }
];

interface ServiceGridsProps {
    services?: ServiceDetailType[];
    isOwnProfile?: boolean;
    userInfo?: { id: string; name: string; avatar: string };
    initialOpenServiceId?: string;
}

const getDisplayValue = (value: string | undefined, defaultValue: string = '可谈') => {
    if (!value || value.trim() === '') return defaultValue;
    return value;
};

const getDisplayPriceValue = (value: string | undefined, defaultValue: string = '可谈') => (
    getDisplayValue(value, defaultValue).replace(/^[¥￥]\s*/, '')
);

const getServiceDisplayOrder = (service: ServiceDetailType, fallbackIndex: number) => {
    if (typeof service.displayOrder === 'number' && Number.isFinite(service.displayOrder)) {
        return service.displayOrder;
    }
    const createdAt = (service as any).createdAt;
    const timestamp = typeof createdAt === 'string' ? new Date(createdAt).getTime() : NaN;
    return Number.isFinite(timestamp) ? -timestamp : fallbackIndex;
};

const sortServicesForProfile = (items: ServiceDetailType[]) => (
    items
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
            const aArchived = a.item.status === 'archived' ? 1 : 0;
            const bArchived = b.item.status === 'archived' ? 1 : 0;
            if (aArchived !== bArchived) return aArchived - bArchived;

            const orderDiff = getServiceDisplayOrder(a.item, a.index) - getServiceDisplayOrder(b.item, b.index);
            if (orderDiff !== 0) return orderDiff;
            return a.index - b.index;
        })
        .map(({ item }, index) => ({ ...item, displayOrder: item.displayOrder ?? index }))
);

const moveItem = <T,>(items: T[], fromIndex: number, toIndex: number) => {
    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
};

const ServiceGrids: React.FC<ServiceGridsProps> = ({ services: propServices, isOwnProfile = false, userInfo, initialOpenServiceId }) => {
    const [selectedService, setSelectedService] = useState<ServiceDetailType | null>(null);
    const [services, setServices] = useState<ServiceDetailType[]>(propServices || []);
    const [loading, setLoading] = useState(true);
    const [draggingServiceId, setDraggingServiceId] = useState<string | null>(null);
    const autoOpenedServiceIdRef = React.useRef<string | null>(null);
    const { showToast } = useToast();

    const loadServices = React.useCallback(async () => {
        if (!userInfo?.id) return;
        setLoading(true);
        try {
            const { serviceService } = await import('../../../services/serviceService');
            const res = await serviceService.getServices({ creatorUserId: userInfo.id, limit: 50, includeArchived: isOwnProfile });
            if (res.data && res.data.services) {
                const mapped = res.data.services.map((s: any) => ({
                    id: s.serviceId || s.id,
                    title: s.title || '',
                    description: s.description || '',
                    priceRange: s.priceRange || '',
                    deliveryTimeRange: s.deliveryTimeRange || '',
                    contentDurationRange: s.contentDurationRange || '',
                    displayOrder: s.displayOrder,
                    tags: s.tags || [],
                    exampleWorks: s.exampleWorks || [],
                    reviews: s.reviews || [],
                    status: s.status
                }));
                setServices(sortServicesForProfile(mapped));
            } else if (!propServices) {
                setServices([]);
            }
        } catch (error) {
            console.error('Failed to fetch services:', error);
            if (!propServices) {
                setServices(MOCK_SERVICES);
            }
        } finally {
            setLoading(false);
        }
    }, [userInfo?.id, propServices, isOwnProfile]);

    React.useEffect(() => {
        if (!userInfo?.id) {
            if (propServices) {
                setServices(sortServicesForProfile(propServices));
                setLoading(false);
            }
            return;
        }
        loadServices();

        const handleServicesUpdated = () => {
            loadServices();
        };

        window.addEventListener('services_updated', handleServicesUpdated);
        return () => window.removeEventListener('services_updated', handleServicesUpdated);
    }, [loadServices, userInfo?.id, propServices]);

    React.useEffect(() => {
        if (!initialOpenServiceId || autoOpenedServiceIdRef.current === initialOpenServiceId) {
            return;
        }

        if (loading) {
            return;
        }

        const matched = services.find((service) => String(service.id) === String(initialOpenServiceId));
        autoOpenedServiceIdRef.current = initialOpenServiceId;

        if (matched) {
            setSelectedService(matched);
            return;
        }

        void (async () => {
            try {
                const { serviceService } = await import('../../../services/serviceService');
                const res = await serviceService.getServiceById(initialOpenServiceId);
                const payload = res.data as any;
                const detail = payload?.data ?? payload;
                if (res.success && detail?.service) {
                    setSelectedService(detail.service);
                }
            } catch (error) {
                console.error('Failed to open shared service:', error);
            }
        })();
    }, [initialOpenServiceId, loading, services]);

    const persistServiceOrder = React.useCallback(async (nextServices: ServiceDetailType[]) => {
        try {
            const { serviceService } = await import('../../../services/serviceService');
            const res = await serviceService.reorderServices(nextServices.map((service) => service.id));
            if (!res.success) {
                throw new Error(res.error || '排序保存失败');
            }
        } catch (error) {
            console.error('Failed to reorder services:', error);
            showToast('服务排序保存失败，已重新加载', 'error');
            loadServices();
        }
    }, [loadServices, showToast]);

    const handleServiceDrop = React.useCallback((targetServiceId: string | number) => {
        if (!isOwnProfile || !draggingServiceId) return;

        const orderedServices = sortServicesForProfile(services);
        const fromIndex = orderedServices.findIndex((service) => String(service.id) === draggingServiceId);
        const toIndex = orderedServices.findIndex((service) => String(service.id) === String(targetServiceId));

        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
            return;
        }

        const nextServices = moveItem(orderedServices, fromIndex, toIndex)
            .map((service, index) => ({ ...service, displayOrder: index }));
        const normalized = sortServicesForProfile(nextServices);
        setServices(normalized);
        void persistServiceOrder(normalized);
    }, [draggingServiceId, isOwnProfile, persistServiceOrder, services]);

    const content = loading ? (
        <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
    ) : services.length === 0 ? (
        <div className="py-20 text-center text-gray-500">暂无服务数据</div>
    ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sortServicesForProfile(services).map((service) => {
                const isArchived = service.status === 'archived';
                return (
                <div
                    key={service.id}
                    draggable={isOwnProfile}
                    onDragStart={(event) => {
                        if (!isOwnProfile) return;
                        setDraggingServiceId(String(service.id));
                        event.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(event) => {
                        if (isOwnProfile) event.preventDefault();
                    }}
                    onDrop={(event) => {
                        event.preventDefault();
                        handleServiceDrop(service.id);
                    }}
                    onDragEnd={() => setDraggingServiceId(null)}
                    onClick={() => setSelectedService(service)}
                    className={`flex flex-col min-h-[220px] relative overflow-hidden border rounded-2xl p-6 group cursor-pointer transition-all duration-300 ${isOwnProfile ? 'cursor-grab active:cursor-grabbing' : ''} ${isArchived
                        ? 'border-gray-200 bg-gray-100/80 opacity-75 hover:border-gray-200 hover:bg-gray-100/80'
                        : 'border-[#EEF1F5] bg-[#FBFBFC] hover:border-[#E6EBF2] hover:bg-white hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(15,23,42,0.05)]'
                        }`}
                >
                    {isArchived && (
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center text-5xl font-black tracking-[0.35em] text-gray-300/60"
                            style={{ transform: 'rotate(-10deg)' }}
                        >
                            已下架
                        </div>
                    )}
                    <div className="relative z-10 flex-1">
                        <div className="flex items-start justify-between gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    {isOwnProfile && (
                                        <span
                                            className="shrink-0 text-gray-300 transition-colors group-hover:text-gray-500"
                                            title="拖动排序"
                                            aria-label="拖动排序"
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            <GripVertical className="h-4 w-4" />
                                        </span>
                                    )}
                                    <h3 className={`text-xl font-semibold transition-colors truncate ${isArchived ? 'text-gray-500' : 'text-gray-900 group-hover:text-blue-600'}`}>
                                        {service.title}
                                    </h3>
                                    {service.status === 'draft' && (
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                            草稿
                                        </span>
                                    )}
                                    {isArchived && (
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-500">
                                            已下架
                                        </span>
                                    )}
                                </div>
                                <p className="mt-3 text-gray-600 text-sm leading-relaxed line-clamp-3 min-h-[4.5em]">
                                    {service.description}
                                </p>
                            </div>
                            <div className="flex flex-col items-end gap-3">
                                <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold ${isArchived ? 'border-gray-200 bg-gray-100 text-gray-500' : 'border-blue-100 bg-blue-50 text-blue-700'}`}>
                                    <span className={`text-sm font-semibold leading-none ${isArchived ? 'text-gray-400' : 'text-blue-500'}`}>¥</span>
                                    {getDisplayPriceValue(service.priceRange)}
                                </div>
                                <div className="flex flex-wrap justify-end gap-2">
                                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 text-xs text-gray-500">
                                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                        {getDisplayValue(service.deliveryTimeRange)}
                                    </span>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 text-xs text-gray-500">
                                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                                        {getDisplayValue(service.contentDurationRange)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="relative z-10 mt-6 flex items-center justify-between">
                        <div className="flex gap-2 flex-wrap max-w-[70%]">
                            {service.tags.slice(0, 4).map(tag => (
                                <span key={tag} className="px-2 py-1 bg-gray-100/50 text-gray-500 text-xs rounded-md border border-gray-100">
                                    {tag}
                                </span>
                            ))}
                            {service.tags.length > 4 && (
                                <span className="px-2 py-1 text-gray-400 text-xs">+{service.tags.length - 4}</span>
                            )}
                        </div>
                        <button className="text-sm font-medium text-black flex items-center gap-1 hover:underline underline-offset-4">
                            查看详情
                        </button>
                    </div>
                </div>
                );
            })}
        </div>
    );

    return (
        <>
            {content}

            {selectedService && (
                <ServiceDetailModal
                    isOpen={!!selectedService}
                    onClose={() => setSelectedService(null)}
                    service={selectedService}
                    isOwnProfile={isOwnProfile}
                    userInfo={userInfo}
                    onSave={(updatedService) => {
                        console.log('Saved service:', updatedService);
                        if (updatedService?.status !== 'draft') {
                            setSelectedService(null);
                        }
                    }}
                />
            )}
        </>
    );
};

export default ServiceGrids;
